import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, MessageSquare, Eye, Download, Trash2, Send, X, Users, FileText, Clock, User, MoreVertical, Settings, Lock, Search, Filter, UserPlus } from 'lucide-react';
import { teamWorkspaceService } from '../lib/teamWorkspaceService';
import { personalizedSummaryService } from '../lib/personalizedSummaryService';
import { userTierService } from '../lib/userTierService';
import { supabase } from '../lib/supabase';
import { ConfirmModal } from '../components/ConfirmModal';
import { useWorkspace, useDeleteWorkspace, useUpdateWorkspace } from '../hooks/useWorkspaces';
import { useWorkspaceSummaries, useShareSummary, useRemoveSharedSummary } from '../hooks/useWorkspaceSummaries';
import { useWorkspaceCollaborators, useInviteWorkspaceCollaborator, useRemoveWorkspaceCollaborator } from '../hooks/useWorkspaceCollaborators';
import { Workspace, WorkspaceSummary, WorkspaceComment, PersonalizedSummary } from '../types';
import { normalizeDocketNumber } from '../utils/federalRegisterHelpers';

export const WorkspacePage: React.FC = () => {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const navigate = useNavigate();
  
  // React Query hooks
  const { data: workspace, isLoading: workspaceLoading, error: workspaceError } = useWorkspace(workspaceId || null);
  const { data: summaries = [], isLoading: summariesLoading, error: summariesError } = useWorkspaceSummaries(workspaceId || null);
  // Comments feature removed
  const shareSummaryMutation = useShareSummary();
  const removeSummaryMutation = useRemoveSharedSummary();
  // Comments feature removed
  const deleteWorkspaceMutation = useDeleteWorkspace();
  const updateWorkspaceMutation = useUpdateWorkspace();
  const { data: collaborators = [], isLoading: collaboratorsLoading } = useWorkspaceCollaborators(workspaceId || null);
  const inviteCollaboratorMutation = useInviteWorkspaceCollaborator();
  const removeCollaboratorMutation = useRemoveWorkspaceCollaborator();
  
  const [editWorkspaceName, setEditWorkspaceName] = useState('');
  const [editWorkspaceDescription, setEditWorkspaceDescription] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'owner' | 'editor' | 'commenter' | 'viewer'>('viewer');
  
  const [userSummaries, setUserSummaries] = useState<PersonalizedSummary[]>([]);
  const [userSummariesLoading, setUserSummariesLoading] = useState(false);
  const loading = workspaceLoading || summariesLoading;
  const [error, setError] = useState<string>('');
  const [userTier, setUserTier] = useState<string>('free');
  const [tierLoading, setTierLoading] = useState(true);
  const [userAccess, setUserAccess] = useState<{ team_role: string; can_share: boolean; can_comment: boolean } | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  
  // Modal states
  const [showShareModal, setShowShareModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState<WorkspaceSummary | null>(null);
  const [summaryContent, setSummaryContent] = useState<string>('');
  const [showOptionsDropdown, setShowOptionsDropdown] = useState(false);
  const [openCollaboratorMenu, setOpenCollaboratorMenu] = useState<string | null>(null);
  const [showEditWorkspaceModal, setShowEditWorkspaceModal] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{ isOpen: boolean; action: (() => void) | null; title: string; message: string }>({
    isOpen: false,
    action: null,
    title: '',
    message: ''
  });
  
  // Form states
  // Comments feature removed
  const [selectedSummaryToShare, setSelectedSummaryToShare] = useState<string>('');
  const [shareNotes, setShareNotes] = useState('');
  // TODO: Future feature - Live document editing within workspaces (far future release)
  const [shareAccessLevel, setShareAccessLevel] = useState<'read' | 'comment'>('read');
  
  // Search and filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAccessLevel, setFilterAccessLevel] = useState<string>('all');
  const [filterType, setFilterType] = useState<string>('all');

  useEffect(() => {
    const checkUserTier = async () => {
      try {
        const tier = await userTierService.getUserTier();
        setUserTier(tier);
      } catch (error) {
        // Error handled silently
      } finally {
        setTierLoading(false);
      }
    };

    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id || null);
    };

    checkUserTier();
    getCurrentUser();
    
    // Load user's personalized summaries when workspace is loaded
    if (workspace?.docket_number) {
      setUserSummariesLoading(true);
      const normalizedDocket = normalizeDocketNumber(workspace.docket_number);
      console.log('Loading summaries for workspace docket:', workspace.docket_number, 'normalized:', normalizedDocket);
      
      personalizedSummaryService.getUserPersonalizedSummaries(normalizedDocket)
        .then((summaries) => {
          console.log('Found summaries:', summaries.length);
          setUserSummaries(summaries);
          setUserSummariesLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load personalized summaries:', err);
          setError(`Failed to load summaries: ${err instanceof Error ? err.message : 'Unknown error'}`);
          setUserSummaries([]);
          setUserSummariesLoading(false);
        });
    } else {
      setUserSummaries([]);
      setUserSummariesLoading(false);
    }
  }, [workspace?.docket_number, workspaceId]);

  // Load user access when workspace is available
  useEffect(() => {
    if (workspaceId && workspace) {
      teamWorkspaceService.getUserWorkspaceAccess(workspaceId)
        .then((access) => {
          console.log('Workspace user access loaded:', access);
          setUserAccess(access);
        })
        .catch((err) => {
          console.error('Failed to load workspace user access:', err);
          // Set to null to indicate access check failed
          setUserAccess(null);
        });
    }
  }, [workspaceId, workspace]);

  // Initialize edit form when opening edit modal
  useEffect(() => {
    if (showEditWorkspaceModal && workspace) {
      setEditWorkspaceName(workspace.name);
      setEditWorkspaceDescription(workspace.description || '');
    }
  }, [showEditWorkspaceModal, workspace]);

  // Handle errors from React Query
  useEffect(() => {
    if (workspaceError) {
      setError(workspaceError instanceof Error ? workspaceError.message : 'Failed to load workspace');
    } else if (summariesError) {
      setError(summariesError instanceof Error ? summariesError.message : 'Failed to load summaries');
    }
  }, [workspaceError, summariesError]);

  // Filter summaries based on search and filters
  const filteredSummaries = useMemo(() => {
    let filtered = summaries;
    
    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(summary =>
        summary.summary_topics.some(topic => topic.toLowerCase().includes(query)) ||
        summary.summary_type.toLowerCase().includes(query) ||
        summary.shared_by_email.toLowerCase().includes(query) ||
        (summary.notes && summary.notes.toLowerCase().includes(query))
      );
    }
    
    // Access level filter
    if (filterAccessLevel !== 'all') {
      filtered = filtered.filter(summary => summary.access_level === filterAccessLevel);
    }
    
    // Type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(summary => summary.summary_type === filterType);
    }
    
    return filtered;
  }, [summaries, searchQuery, filterAccessLevel, filterType]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.options-dropdown')) {
        setShowOptionsDropdown(false);
      }
      if (!target.closest('.collaborator-menu-dropdown')) {
        setOpenCollaboratorMenu(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);


  const handleShareSummary = async () => {
    if (!selectedSummaryToShare || !workspaceId) {
      setError('Please select a summary to share');
      return;
    }

    try {
      setError('');
      await shareSummaryMutation.mutateAsync({
        workspaceId,
        summaryId: selectedSummaryToShare,
        accessLevel: shareAccessLevel,
        notes: shareNotes.trim() || undefined
      });

      setSelectedSummaryToShare('');
      setShareNotes('');
      setShareAccessLevel('read');
      setShowShareModal(false);
      // Success - summaries list will refresh automatically via React Query
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share summary');
    }
  };

  const handleViewSummary = async (summary: WorkspaceSummary) => {
    try {
      setError('');
      const content = await teamWorkspaceService.getSharedSummaryContent(summary.shared_summary_id);
      setSelectedSummary(summary);
      setSummaryContent(content);
      setShowSummaryModal(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load summary content');
    }
  };

  // Comments feature removed

  const handleRemoveSummary = async (sharedSummaryId: string) => {
    setConfirmModal({
      isOpen: true,
      action: async () => {
        try {
          setError('');
          await removeSummaryMutation.mutateAsync({ sharedSummaryId, workspaceId: workspaceId! });
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to remove summary');
        }
      },
      title: 'Remove Summary',
      message: 'Are you sure you want to remove this summary from the workspace?'
    });
  };

  const handleDeleteWorkspace = async () => {
    if (!workspace) return;
    
    setConfirmModal({
      isOpen: true,
      action: async () => {
        try {
          setError('');
          await deleteWorkspaceMutation.mutateAsync(workspace.id);
          navigate('/workspaces');
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to delete workspace');
        }
      },
      title: 'Delete Workspace',
      message: 'Are you sure you want to delete this workspace? This action cannot be undone and will delete all shared summaries.'
    });
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTopics = (topics: string[]) => {
    return topics.join(', ');
  };

  const getAccessLevelColor = (level: string) => {
    switch (level) {
      case 'comment': return 'bg-blue-100 text-blue-800';
      case 'read': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Show loading state while checking user tier
  if (tierLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mx-auto"></div>
          <p className="text-gray-600 mt-4">Loading workspace...</p>
        </div>
      </div>
    );
  }

  if (!workspace) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Workspace not found or access denied</p>
          <button
            onClick={() => navigate('/workspaces')}
            className="mt-4 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors"
          >
            Back to Workspaces
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <nav className="flex items-center space-x-2 text-sm text-gray-600">
                  <button
                    onClick={() => navigate('/workspaces')}
                    className="text-orange-600 hover:text-orange-700 transition-colors"
                  >
                    Workspaces
                  </button>
                  <span>/</span>
                  <span className="text-gray-900 font-medium">{workspace.name}</span>
                </nav>
              </div>
              <h1 className="text-3xl font-bold text-gray-900">{workspace.name}</h1>
              <p className="text-gray-600 mt-2">
                Docket {workspace.docket_number} • {summaries.length} shared summaries
              </p>
              {workspace.description && (
                <p className="text-gray-600 mt-1">{workspace.description}</p>
              )}
            </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={async () => {
                    if (!workspaceId) return;
                    try {
                      setError('');
                      await teamWorkspaceService.exportWorkspaceSummaries(workspaceId);
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to export workspace');
                    }
                  }}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-medium rounded-lg transition-colors flex items-center space-x-2"
                  title="Export all summaries as PDF"
                >
                  <Download className="w-5 h-5" />
                  <span>Export</span>
                </button>
                
                {userAccess?.can_share && (
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white font-medium rounded-lg transition-colors flex items-center space-x-2"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Share Summary</span>
                  </button>
                )}
                
                {/* Options Dropdown */}
                <div className="relative options-dropdown">
                  <button
                    onClick={() => setShowOptionsDropdown(!showOptionsDropdown)}
                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    title="Workspace Options"
                  >
                    <MoreVertical className="w-5 h-5" />
                  </button>
                  
                  {showOptionsDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                      <div className="py-1">
                        {userAccess?.team_role === 'owner' || userAccess?.team_role === 'admin' ? (
                          <button
                            onClick={() => {
                              setShowOptionsDropdown(false);
                              setShowEditWorkspaceModal(true);
                            }}
                            className="w-full flex items-center space-x-3 px-4 py-2 text-left text-gray-700 hover:bg-gray-50 transition-colors"
                          >
                            <Settings className="w-4 h-4" />
                            <span>Edit Workspace</span>
                          </button>
                        ) : null}
                        {userAccess?.team_role === 'owner' && (
                          <button
                            onClick={() => {
                              setShowOptionsDropdown(false);
                              handleDeleteWorkspace();
                            }}
                            className="w-full flex items-center space-x-3 px-4 py-2 text-left text-red-600 hover:bg-red-50 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                            <span>Delete Workspace</span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-800 text-sm">{error}</p>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Shared Summaries */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">Shared Summaries</h2>
                  <div className="flex items-center space-x-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search summaries..."
                        className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm w-48"
                      />
                    </div>
                    <select
                      value={filterAccessLevel}
                      onChange={(e) => setFilterAccessLevel(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    >
                      <option value="all">All Access</option>
                      <option value="read">Read Only</option>
                      <option value="comment">Can Comment</option>
                    </select>
                    <select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
                    >
                      <option value="all">All Types</option>
                      {[...new Set(summaries.map(s => s.summary_type))].map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              
              <div className="p-6">
                {summaries.length === 0 ? (
                  <div className="text-center py-8">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No summaries shared yet</p>
                    <p className="text-sm text-gray-500 mt-1">Share your first summary to start collaborating</p>
                  </div>
                ) : filteredSummaries.length === 0 ? (
                  <div className="text-center py-8">
                    <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No summaries match your filters</p>
                    <button
                      onClick={() => {
                        setSearchQuery('');
                        setFilterAccessLevel('all');
                        setFilterType('all');
                      }}
                      className="text-sm text-orange-600 hover:text-orange-700 mt-2"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {filteredSummaries.map((summary) => (
                      <div key={summary.shared_summary_id} className="p-4 border border-gray-200 rounded-lg hover:border-orange-300 transition-colors">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className={`px-2 py-1 text-xs rounded-full ${getAccessLevelColor(summary.access_level)}`}>
                                {summary.access_level}
                              </span>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {summary.summary_type}
                              </span>
                            </div>
                            
                            <h4 className="font-medium text-gray-900 mb-1">
                              Topics: {formatTopics(summary.summary_topics)}
                            </h4>
                            
                            {summary.notes && (
                              <p className="text-gray-600 mb-2 text-sm">{summary.notes}</p>
                            )}
                            
                            <div className="flex items-center space-x-4 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <FileText className="w-4 h-4" />
                                <span>{summary.document_count} documents</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <User className="w-4 h-4" />
                                <span>{summary.shared_by_email}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <Clock className="w-4 h-4" />
                                <span>{formatDate(summary.shared_at)}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewSummary(summary)}
                              className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                              title="View Summary"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            {summary.shared_by === currentUserId && (
                              <button
                                onClick={() => handleRemoveSummary(summary.shared_summary_id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                                title="Remove Summary"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Sidebar */}
          <div className="lg:col-span-1 space-y-8">
            {/* Collaborators */}
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-medium text-gray-900">Collaborators</h2>
                {userAccess?.team_role === 'owner' && (
                  <button
                    onClick={() => setShowInviteModal(true)}
                    className="text-sm text-orange-600 hover:text-orange-700 font-medium"
                  >
                    <UserPlus className="w-4 h-4 inline mr-1" />
                    Invite
                  </button>
                )}
              </div>
              <div className="p-6">
                {collaboratorsLoading ? (
                  <div className="text-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-600 mx-auto"></div>
                  </div>
                ) : collaborators.length === 0 ? (
                  <div className="text-center py-4">
                    <Users className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600 text-sm">No collaborators yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {collaborators.map((collaborator) => (
                      <div key={collaborator.id} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                              <span className="text-xs font-medium text-orange-700">
                                {(collaborator.user_email || 'U').charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {collaborator.user_email || 'Unknown'}
                              </p>
                              <p className="text-xs text-gray-500 capitalize">{collaborator.role}</p>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            collaborator.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {collaborator.status}
                          </span>
                          {userAccess?.team_role === 'owner' && 
                           collaborator.user_id !== currentUserId && 
                           workspaceId && (
                            <div className="relative collaborator-menu-dropdown">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenCollaboratorMenu(
                                    openCollaboratorMenu === collaborator.id ? null : collaborator.id
                                  );
                                }}
                                className="p-1 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded transition-colors"
                                title="Collaborator Options"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                              
                              {openCollaboratorMenu === collaborator.id && (
                                <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                                  <div className="py-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenCollaboratorMenu(null);
                                        setConfirmModal({
                                          isOpen: true,
                                          action: async () => {
                                            try {
                                              setError('');
                                              await removeCollaboratorMutation.mutateAsync({
                                                workspaceId,
                                                collaboratorId: collaborator.id
                                              });
                                            } catch (err) {
                                              setError(err instanceof Error ? err.message : 'Failed to remove collaborator');
                                            }
                                          },
                                          title: 'Remove Collaborator',
                                          message: `Are you sure you want to remove ${collaborator.user_email || 'this collaborator'} from the workspace?`
                                        });
                                      }}
                                      className="w-full flex items-center space-x-3 px-4 py-2 text-left text-red-600 hover:bg-red-50 transition-colors"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                      <span>Remove</span>
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Activity Feed and Comments features removed */}
          </div>
        </div>
      </div>

      {/* Share Summary Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Share Summary</h3>
              <button
                onClick={() => {
                  setShowShareModal(false);
                  setSelectedSummaryToShare('');
                  setShareNotes('');
                  setShareAccessLevel('read');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <div className="block text-sm font-medium text-gray-700 mb-1">
                    Select Summary *
                  </div>
                  {userSummariesLoading ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-orange-600 mr-2"></div>
                      <span className="text-sm text-gray-600">Loading summaries...</span>
                    </div>
                  ) : userSummaries.length === 0 ? (
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-yellow-50">
                      <p className="text-sm text-yellow-800">
                        No summaries found for docket {workspace?.docket_number}. 
                        {workspace?.docket_number && (
                          <span className="block mt-1 text-xs">
                            Create a personalized summary for this docket first, then you can share it here.
                          </span>
                        )}
                      </p>
                    </div>
                  ) : (
                    <select
                      value={selectedSummaryToShare}
                      onChange={(e) => setSelectedSummaryToShare(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    >
                      <option value="">Choose a summary...</option>
                      {userSummaries.map((summary) => (
                        <option key={summary.id} value={summary.id}>
                          {summary.topics.join(', ')} ({summary.summary_type})
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                
                <div>
                  <label htmlFor="fld2-WorkspacePagetsx-0" className="block text-sm font-medium text-gray-700 mb-1">
                    Access Level
                  </label>
                  <select id="fld2-WorkspacePagetsx-0"
                    value={shareAccessLevel}
                    onChange={(e) => setShareAccessLevel(e.target.value as 'read' | 'comment')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="read">Read Only</option>
                    <option value="comment">Can Comment</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-WorkspacePagetsx-0">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={shareNotes}
                    id="fld-WorkspacePagetsx-0" onChange={(e) => setShareNotes(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Add notes about this summary..."
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowShareModal(false);
                    setSelectedSummaryToShare('');
                    setShareNotes('');
                    setShareAccessLevel('read');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleShareSummary}
                  disabled={shareSummaryMutation.isPending || userSummariesLoading || userSummaries.length === 0 || !selectedSummaryToShare}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {shareSummaryMutation.isPending ? 'Sharing...' : 'Share Summary'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invite Collaborator Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Invite Collaborator</h3>
              <button
                onClick={() => {
                  setShowInviteModal(false);
                  setInviteEmail('');
                  setInviteRole('viewer');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-WorkspacePagetsx-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={inviteEmail}
                    id="fld-WorkspacePagetsx-1" onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="user@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-WorkspacePagetsx-2">
                    Role
                  </label>
                  <select
                    value={inviteRole}
                    id="fld-WorkspacePagetsx-2" onChange={(e) => setInviteRole(e.target.value as 'owner' | 'editor' | 'commenter' | 'viewer')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    <option value="viewer">Viewer - Can view summaries</option>
                    <option value="commenter">Commenter - Can view and comment</option>
                    <option value="editor">Editor - Can view, comment, and share</option>
                    <option value="owner">Owner - Full access</option>
                  </select>
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowInviteModal(false);
                    setInviteEmail('');
                    setInviteRole('viewer');
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!inviteEmail.trim() || !workspaceId) {
                      setError('Please enter an email address');
                      return;
                    }
                    try {
                      setError('');
                      await inviteCollaboratorMutation.mutateAsync({
                        workspaceId,
                        userEmail: inviteEmail.trim(),
                        role: inviteRole
                      });
                      setShowInviteModal(false);
                      setInviteEmail('');
                      setInviteRole('viewer');
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to invite collaborator');
                    }
                  }}
                  disabled={inviteCollaboratorMutation.isPending}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors disabled:opacity-50"
                >
                  {inviteCollaboratorMutation.isPending ? 'Sending...' : 'Send Invitation'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Summary Modal */}
      {showSummaryModal && selectedSummary && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Shared Summary</h3>
                <p className="text-sm text-gray-600">
                  Topics: {formatTopics(selectedSummary.summary_topics)}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowSummaryModal(false);
                  setSelectedSummary(null);
                  setSummaryContent('');
                }}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="prose max-w-none">
                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-500">
                    Shared by: {selectedSummary.shared_by_email}<br />
                    Access Level: {selectedSummary.access_level}<br />
                    Shared: {formatDate(selectedSummary.shared_at)}
                  </p>
                </div>
                <div className="whitespace-pre-wrap text-gray-700">
                  {summaryContent}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, action: null, title: '', message: '' })}
        onConfirm={() => {
          if (confirmModal.action) {
            confirmModal.action();
          }
        }}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText="Confirm"
        cancelText="Cancel"
      />

      {/* Edit Workspace Modal */}
      {showEditWorkspaceModal && workspace && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Edit Workspace</h3>
              <button
                onClick={() => setShowEditWorkspaceModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-WorkspacePagetsx-3">
                    Workspace Name *
                  </label>
                  <input
                    type="text"
                    value={editWorkspaceName}
                    id="fld-WorkspacePagetsx-3" onChange={(e) => setEditWorkspaceName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter workspace name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="fld-WorkspacePagetsx-4">
                    Description
                  </label>
                  <textarea
                    value={editWorkspaceDescription}
                    id="fld-WorkspacePagetsx-4" onChange={(e) => setEditWorkspaceDescription(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    placeholder="Enter workspace description (optional)"
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex items-center justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditWorkspaceModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!editWorkspaceName.trim()) {
                      setError('Workspace name is required');
                      return;
                    }
                    try {
                      setError('');
                      await updateWorkspaceMutation.mutateAsync({
                        workspaceId: workspace.id,
                        updates: {
                          name: editWorkspaceName.trim(),
                          description: editWorkspaceDescription.trim() || undefined
                        }
                      });
                      setShowEditWorkspaceModal(false);
                    } catch (err) {
                      setError(err instanceof Error ? err.message : 'Failed to update workspace');
                    }
                  }}
                  className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-md transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
